import classNames from 'classnames';
import { HTMLRenderer } from 'common/html-renderer.js';
import { ImageResponsive } from 'common/image/image-responsive.js';
import { TileBodyHeaderV2 } from 'common/tile/tile-body-header-v2.js';
import { Tile } from 'common/tile/tile.js';
import { TabHeaderSmall } from 'components.layout/tab.header.small.js';
import { ICaptureEventProps } from 'components/tracking/types.js';
import { ColumnLayoutContext } from 'page-context/column-layout/column-layout-context.js';
import { useContext, useState } from 'react';
import { IModule } from 'redux-state/layout/types.js';
import { IColumnLayout } from 'redux-state/layout/types.modules.js';
import { IResponsiveImage } from 'utilities/image/image.js';

interface IHtmlTab {
  heading: string;
  subHeading?: string;
  text: string;
  sponsor?: Array<IResponsiveImage>;
}

interface ILayoutTabs {
  showWhen: IColumnLayout;
  height: number;
}

export interface ITabbedProps {
  content: Array<IHtmlTab>;
  tabLayout?: Array<ILayoutTabs>;
  tracking?: ICaptureEventProps;
}

export const HtmlTabsModule = (props: IModule<ITabbedProps>) => {
  const { content, tabLayout, tracking } = props.config;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const columnLayout = useContext(ColumnLayoutContext);

  const { text, sponsor } = content[activeTabIndex];
  const sponsorWithTracking = sponsor && tracking ? sponsor.map((image) => ({ ...image, tracking })) : sponsor;

  const contentHeight = tabLayout?.find(({ showWhen }) => showWhen === columnLayout)?.height;
  const contentStyle = contentHeight ? { minHeight: `${contentHeight}px` } : {};

  return (
    <Tile dataModule="tabbed-html-renderer" {...props}>
      <div className="Tile-body">
        <TileBodyHeaderV2 isDrawer={props.isDrawer} modulePath={props.modulePath} config={props.config} />

        <ul role="tablist" className="TabbedHtmlRenderer-tabs Tab Tab--slim">
          {content.map(({ heading, subHeading }, index) => {
            const isTabActive = index === activeTabIndex;
            return (
              <li className="TabbedHtmlRenderer-tab" key={`${heading}${index}`}>
                <button
                  role="tab"
                  className={classNames('TabbedHtmlRenderer-button Tab-item', { 'is-active': isTabActive })}
                  onClick={() => setActiveTabIndex(index)}
                  aria-selected={isTabActive}
                  aria-controls={`panel-${index}`}
                  id={`tab-${index}`}
                >
                  <TabHeaderSmall title={heading} subTitle={subHeading} />
                </button>
              </li>
            );
          })}
        </ul>

        <div
          id={`panel-${activeTabIndex}`}
          key={activeTabIndex}
          className="TabbedHtmlRenderer-content"
          aria-labelledby={`tab-${activeTabIndex}`}
          role="tabpanel"
          style={contentStyle}
        >
          <HTMLRenderer content={text} classes="u-mT-responsive" />
        </div>
      </div>
      {sponsorWithTracking && <ImageResponsive images={sponsorWithTracking} />}
    </Tile>
  );
};
